import type { ReactNode } from "react";

export function StatCard({ label, value, tone = "default", hint }: { label: string; value: string | number; tone?: "default" | "danger" | "warn" | "success"; hint?: string }) {
  const toneClass = {
    default: "border-gm-border",
    danger: "border-gm-danger/50",
    warn: "border-gm-gold/50",
    success: "border-gm-success/50",
  }[tone];
  return (
    <div className={`rounded-2xl border ${toneClass} bg-gm-panel p-4`}>
      <div className="text-xs font-medium text-gm-muted">{label}</div>
      <div className="mt-1 text-2xl font-extrabold text-gm-text">{value}</div>
      {hint && <div className="mt-1 text-[11px] text-gm-muted">{hint}</div>}
    </div>
  );
}

const STATUS_LABELS: Record<string, string> = {
  NEW: "Новый заказ",
  ASSIGNED: "Назначен",
  AWAITING_PAYMENT: "Ожидает оплаты",
  PAYMENT_REVIEW: "Проверить оплату",
  PAYMENT_NOT_FOUND: "Оплата не найдена",
  PAYMENT_CONFIRMED: "Оплата подтверждена",
  AWAITING_CODE: "Ожидает код",
  CODE_SUBMITTED: "Код отправлен",
  CODE_REVIEW: "Проверить код",
  INVALID_CODE: "Неверный код",
  ACTIVATION_IMPOSSIBLE: "Активация невозможна",
  REFUND_PENDING: "Возврат ожидает",
  REFUND_PROCESSING: "Возврат в процессе",
  REFUNDED: "Возвращено",
  COMPLETED: "Завершено",
  EXPIRED: "Истёк",
  CANCELLED: "Отменён",
};

const STATUS_COLORS: Record<string, string> = {
  NEW: "bg-blue-500/15 text-blue-300",
  AWAITING_PAYMENT: "bg-neutral-500/15 text-neutral-300",
  PAYMENT_REVIEW: "bg-gm-danger/20 text-gm-danger",
  PAYMENT_NOT_FOUND: "bg-gm-danger/20 text-gm-danger",
  AWAITING_CODE: "bg-violet-500/15 text-violet-300",
  CODE_REVIEW: "bg-gm-gold/20 text-gm-gold",
  INVALID_CODE: "bg-gm-danger/20 text-gm-danger",
  ACTIVATION_IMPOSSIBLE: "bg-gm-danger/20 text-gm-danger",
  REFUND_PENDING: "bg-gm-danger/20 text-gm-danger",
  COMPLETED: "bg-gm-success/20 text-gm-success",
};

export function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${STATUS_COLORS[status] ?? "bg-gm-panel2 text-gm-muted"}`}>
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

export function OrderCard({ order, footer }: { order: any; footer?: ReactNode }) {
  return (
    <div className="rounded-xl border border-gm-border bg-gm-panel2 p-3">
      <div className="flex items-center justify-between">
        <span className="font-mono text-sm font-bold text-gm-gold">#{order.shortId}</span>
        <StatusBadge status={order.status} />
      </div>
      <div className="mt-2 text-sm text-gm-text">{order.packageGbSnapshot} ГБ · {order.packagePriceSnapshot} ₸</div>
      <div className="text-xs text-gm-muted">{order.phone}</div>
      {footer && <div className="mt-3">{footer}</div>}
    </div>
  );
}

export function Btn({ children, onClick, tone = "accent", disabled }: { children: ReactNode; onClick?: () => void; tone?: "accent" | "success" | "danger" | "muted"; disabled?: boolean }) {
  const cls = {
    accent: "bg-gm-accent hover:brightness-110",
    success: "bg-gm-success text-gm-bg hover:brightness-110",
    danger: "bg-gm-danger hover:brightness-110",
    muted: "bg-gm-panel2 border border-gm-border text-gm-text hover:bg-gm-border",
  }[tone];
  return (
    <button onClick={onClick} disabled={disabled} className={`w-full rounded-lg px-3 py-2 text-sm font-bold text-white transition disabled:opacity-40 ${cls}`}>
      {children}
    </button>
  );
}
